import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { UtilityService } from '@eforall/common';
import { CallLink, EmailLink, LinkButtonWidget, SmsLink } from '@eforall/dashboard';
import { ExploredPerson, NotecategoryId, NounIcon } from '@interfaces';
import { KENDO_LAYOUT } from '@progress/kendo-angular-layout';
import { StaticDataService } from '../../../../services';
import { PersonExplorerService } from '../person-explorer.service';

interface PersonChip {
	label: 'Red Flag' | 'Do Not Contact' | 'Staff' | 'Open Engagement' | 'Volunteer' | 'Entrepreneur' | 'Applicant' | 'Awardee' | 'Donor',
	value?: string,
	color: 'color-redflag' | 'color-gray' | 'color-eforall-navy' | 'color-eforall-blue' | 'color-green' | 'color-darkgold'
}
interface PersonJourney {
	label: string,
	value: string,
	status?: string
}
interface PersonDetails {
	icon: string,
	fullName: string,
	chips: PersonChip[],
	contactMethod: string | undefined,
	emailLink: EmailLink,
	phoneLink: CallLink | undefined,
	smsLink: SmsLink | undefined,
	agreements: { agreement: string, version: number, formattedUpdatedDate: string }[],
	formattedCreatedDate: string,
	howLearn: string,
	formattedLastLoginDate: string,
	entrepreneurialJourney: PersonJourney[],
	counts: {
		engagements: number,
		volunteerings: number,
		accApplications: number,
		notes: number,
		awardsAmount: string,
		contactRequests: number,
		emailsSent: number,
	}
}


@Component({
	selector: 'person-overview-part',
	standalone: true,
	imports: [
		KENDO_LAYOUT,
		LinkButtonWidget
	],
	changeDetection: ChangeDetectionStrategy.Eager,
	templateUrl: './person-overview.part.html',
	styleUrl: './person-overview.part.scss'
})

export class PersonOverviewPart {

	#util = inject(UtilityService);
	#staticDataService = inject(StaticDataService);
	personExplorer = inject(PersonExplorerService);

	personDetails = computed<PersonDetails | undefined>(() => {
		const person = this.personExplorer.person.value();
		if (!person) return undefined;

		const totalAwardedAmount = person.entrepreneurship.reduce((total, e) => total + (e.awardedAmount ?? 0), 0);

		return {
			icon: `fa-light ${NounIcon.Person}`,
			fullName: person.general.fullName,
			chips: this.getChips(person, totalAwardedAmount),
			contactMethod: person.general.contactMethodId ? this.#staticDataService.contact_methods.byId(person.general.contactMethodId)?.name : undefined,
			emailLink: { linkType: 'email', email: person.general.primaryEmail },
			phoneLink: person.general.phone ? { linkType: 'call', phone: person.general.phone } : undefined,
			smsLink: person.general.phone ? { linkType: 'sms', phone: person.general.phone } : undefined,
			agreements: person.signedAgreements.map(a => ({
				...a,
				formattedUpdatedDate: this.formatUTCDate(a.updatedUTC)
			})).sort((a, b) => b.updatedUTC - a.updatedUTC),
			formattedCreatedDate: this.formatUTCDate(person.createdUTC),
			howLearn: person.general.howLearn,
			formattedLastLoginDate: person.lastLoginUTC ? this.formatUTCDate(person.lastLoginUTC) : 'Never logged in to Public App',
			entrepreneurialJourney: this.getEntrepreneurialJourney(person),
			counts: {
				engagements: person.engagements.length,
				volunteerings: person.volunteering.length,
				accApplications: person.entrepreneurship.filter(e => e.role === 'Application').length,
				notes: person.notes.length,
				awardsAmount: totalAwardedAmount.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }),
				contactRequests: person.contactRequests.length,
				emailsSent: person.emailsSent.length,
			}
		};
	});

	formatUTCDate(utc: number | undefined): string {
		if (!utc) return 'N/A';
		return this.#util.date.formatUTC(utc, 'MM/DD/YYYY', 'No Time', 'en-US');
	}

	getChips(person: ExploredPerson, totalAwardedAmount: number): PersonChip[] {
		const chips: PersonChip[] = [];

		if (person.notes.some(n => n.noteCategoryId === NotecategoryId.RedFlag)) {
			chips.push({ label: 'Red Flag', color: 'color-redflag' });
		}

		if (person.general.doNotContactNote) {
			chips.push({ label: 'Do Not Contact', color: 'color-redflag' });
		}

		if (person.staff) {
			chips.push({
				label: 'Staff',
				value: person.staff.status,
				color: person.staff.status === 'Current' ? 'color-eforall-navy' : 'color-gray'
			});
		}

		if (person.engagements.some(e => !e.closedUTC)) {
			chips.push({ label: 'Open Engagement', color: 'color-green' });
		}

		if (person.volunteering.some(v => v.trimesterId === person.trimesterWindowIds?.programmingTrimesterId)) {
			chips.push({ label: 'Volunteer', value: 'Current', color: 'color-green' });
		} else if (person.volunteer?.isApproved && person.volunteering.length === 0) {
			chips.push({ label: 'Volunteer', value: 'ApprovedButNeverEngaged', color: 'color-gray' });
		} else if (person.volunteering.length > 0) {
			chips.push({ label: 'Volunteer', value: 'Past', color: 'color-gray' });
		}

		const ventureItems = person.entrepreneurship.filter(e => e.role === 'Venture');
		if (ventureItems.some(e => e.isVentureDroppedOut)) {
			chips.push({ label: 'Entrepreneur', value: 'Dropout', color: 'color-redflag' });
		} else if (ventureItems.some(e => e.trimesterId === person.trimesterWindowIds?.programmingTrimesterId)) {
			chips.push({ label: 'Entrepreneur', value: 'Current', color: 'color-green' });
		} else if (ventureItems.length > 0) {
			chips.push({ label: 'Entrepreneur', value: 'Alumni', color: 'color-green' });
		}

		const applicationItems = person.entrepreneurship.filter(e => e.role === 'Application');
		if (applicationItems.some(e => e.trimesterId === person.trimesterWindowIds?.admissionsTrimesterId)) {
			chips.push({ label: 'Applicant', value: 'Current', color: 'color-green' });
		} else if (applicationItems.some(e => e.trimesterId === person.trimesterWindowIds?.prevAdmissionsTrimesterId && e.isApplicationDeferred)) {
			chips.push({ label: 'Applicant', value: 'Deferred', color: 'color-eforall-blue' });
		}

		if (totalAwardedAmount > 0) {
			chips.push({ label: 'Awardee', color: 'color-darkgold' });
		}


		return chips;
	}



	/**
	 * Always show first and last accApplications.
	 * Label them as "Accepted Application" if the application is accepted; otherwise use "Current Application" for the current admissions period, or "1st Application"/"Last Application".
	 * Also show the accApplications in between the first and last, but only if they are accepted.
	 * And show the corresponding venture details if the application is accepted.
	 */
	getEntrepreneurialJourney(person: ExploredPerson) {
		const journey: PersonJourney[] = [];

		journey.push({ label: 'Account Created', value: this.formatUTCDate(person.createdUTC) });

		journey.push({ label: 'Lead Created', value: this.formatUTCDate(person.entrepreneurLead?.createdUTC) });

		const admissionsTrimesterId = person.trimesterWindowIds?.admissionsTrimesterId;

		const groupedAcceleratorItems = this.#util.array.toArrayMap(
			person.entrepreneurship.filter(e => e.programType === 'Accelerator'),
			e => e.role
		);

		const applications = (groupedAcceleratorItems.Application ?? []).sort((a, b) => a.trimesterId - b.trimesterId);
		const ventureByTrimesterId = this.#util.array.toMap(groupedAcceleratorItems.Venture || [], v => v.trimesterId);

		const getApplicationLabel = (applicationTrimesterId: number, index: number) => {

			const hasVenture = !!ventureByTrimesterId[applicationTrimesterId];

			if (index === 0) {
				if (hasVenture) return 'Accepted Application';
				return applicationTrimesterId === admissionsTrimesterId ? 'Current Application' : '1st Application';
			}

			if (index === applications.length - 1) {
				if (hasVenture) return 'Accepted Application';
				return applicationTrimesterId === admissionsTrimesterId ? 'Current Application' : 'Last Application';
			}

			return hasVenture ? 'Accepted Application' : undefined;
		};

		applications.forEach((application, index) => {

			const label = getApplicationLabel(application.trimesterId, index);

			if (label) {

				journey.push({ label, value: `${this.formatUTCDate(application.createdUTC)} (${application.trimesterName})`, status: application.status });

				const venture = ventureByTrimesterId[application.trimesterId];

				if (!venture) return;

				journey.push({ label: 'Cohort', value: venture.programName ?? 'N/A', status: venture.status });

				if (venture.awardedAmount) {
					const status = venture.awardedAmount ? `${venture.awardedAmount.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })}` : undefined;
					journey.push({ label: 'Alum Awards', value: venture.trimesterName, status });
				}
			}
		});

		return journey;

	}


}